
// src/components/profile/identity-verification/hooks/useDocumentRequirements.ts

import { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormValues } from "../schema/documentValidationSchema";
import { IDDocumentType } from "@/hooks/admin/identity-docs/useIDTypes";

export interface DocumentRequirement {
  type: string;
  description: string;
  fulfilled: boolean;
  countryCode?: string;
  documentTypeFilter?: (type: any) => boolean;
  locked?: boolean;
  excludeCountries?: string[];
}

export function useDocumentRequirements(
  verificationRules: any,
  idTypes: IDDocumentType[] | undefined,
  verificationCountry: string,
  residencyStatus: string,
  form: UseFormReturn<FormValues>
) {
  const [documentsNeeded, setDocumentsNeeded] = useState<DocumentRequirement[]>([]);

  // Determine required documents based on rules and residency status
  useEffect(() => {
    if (!verificationRules || !idTypes) return;

    const isExpatriate = residencyStatus === "expatriate";
    const needed: DocumentRequirement[] = [];

    // Only add primary document for residents, not for expatriates
    if (verificationRules.requires_primary_document && !isExpatriate) {
      needed.push({
        type: "primary",
        description: "Primary identification document (passport, national ID)",
        fulfilled: false,
        countryCode: verificationCountry,
        locked: true, // lock for residents
        documentTypeFilter: (type: any) => type.proves_citizenship === true,
      });
    }

    if (isExpatriate && verificationRules.expatriate_requires_passport) {
      needed.push({
        type: "passport",
        description: "Passport from your country of citizenship",
        fulfilled: false,
        // Don't preset country code for passport, let user select their citizenship country
        locked: false, // Explicitly allow country selection for passports
        documentTypeFilter: (type: any) => type.proves_citizenship === true,
        excludeCountries: [verificationCountry], // Exclude verification country from options
      });
    }

    if (isExpatriate && verificationRules.expatriate_requires_residency) {
      needed.push({
        type: "residency",
        description:
          "Legal residency document for the country you are verifying for",
        fulfilled: false,
        countryCode: verificationCountry,
        locked: true, // Lock the country for residency document
        documentTypeFilter: (type: any) => type.proves_residency === true,
      });
    }

    // Reset form and initialize sections based on needed documents
    const initialSections = needed.map((doc) => ({
      country_code: doc.countryCode || "",
      document_type_id: "",
      document_number: "",
      issue_date: undefined,
      expiry_date: undefined,
      verification_purpose: doc.type as any,
      document_file: undefined,
    }));

    // Only reset if the sections count has changed
    if (form.getValues("sections")?.length !== initialSections.length) {
      form.reset({ sections: initialSections });
    }

    setDocumentsNeeded(needed);
  }, [
    verificationRules,
    residencyStatus,
    verificationCountry,
    idTypes,
    form,
  ]);

  // Update documentsNeeded fulfilled status
  useEffect(() => {
    const sections = form.getValues("sections");
    if (!sections || !documentsNeeded.length || !idTypes) return;

    const updatedNeeded = documentsNeeded.map((doc, index) => {
      // A document is fulfilled if the corresponding section has all required fields
      const section = sections[index];
      let fulfilled = false;

      if (
        section &&
        section.country_code &&
        section.document_type_id &&
        section.document_number &&
        section.document_file
      ) {
        // Additional check for specific document types
        if (doc.documentTypeFilter) {
          const selectedType = idTypes.find(
            (type) => type.id === section.document_type_id
          );
          fulfilled = selectedType
            ? doc.documentTypeFilter(selectedType)
            : false;
        } else {
          fulfilled = true;
        }
      }

      return { ...doc, fulfilled };
    });

    setDocumentsNeeded(updatedNeeded);
  }, [form.watch(), documentsNeeded, idTypes]);

  return { documentsNeeded };
}
